import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { videoAPI, vocabularyAPI } from '../api';
import { getMediaUrl } from '../utils/config';

const AdminVocabulary = () => {
  const navigate = useNavigate();
  const { lessonId } = useParams();
  const location = useLocation();
  
  // 从URL获取videoId参数
  const queryParams = new URLSearchParams(location.search);
  const videoIdParam = queryParams.get('videoId');
  const isDirectEntry = !!videoIdParam;
  
  // 状态
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const [subtitles, setSubtitles] = useState([]);
  const [selectedWords, setSelectedWords] = useState([]);
  const [selectedPhrases, setSelectedPhrases] = useState([]);
  const [selectedExpressions, setSelectedExpressions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingWord, setEditingWord] = useState(null);
  const [editingPhrase, setEditingPhrase] = useState(null);
  const [editingExpression, setEditingExpression] = useState(null);
  const [editForm, setEditForm] = useState({
    phonetic: '',
    partOfSpeech: '',
    definition: '',
    chineseTranslation: '',
    synonyms: '',
    cambridgeLevel: '剑桥一级'
  });
  const [phraseEditForm, setPhraseEditForm] = useState({
    phonetic: '',
    partOfSpeech: '',
    chineseTranslation: '',
    definition: '',
    synonyms: ''
  });
  const [expressionEditForm, setExpressionEditForm] = useState({
    phonetic: '',
    partOfSpeech: '',
    chineseTranslation: '',

    definition: '',
    synonyms: ''
  });
  const [vocabularyAnalysis, setVocabularyAnalysis] = useState(null);
  const [extractingVocabulary, setExtractingVocabulary] = useState(false);

  // 十大主题分类及其子分类
  const categories = [
    { value: 'all', label: '全部分类', children: [] },
    { 
      value: 'universe', 
      label: '宇宙与空间科学', 
      children: [
        { value: 'celestial', label: '天体' },
        { value: 'galaxy', label: '星系' },
        { value: 'blackhole', label: '黑洞' },
        { value: 'cosmic_origin', label: '宇宙起源' },
        { value: 'space_exploration', label: '太空探索' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'physics', 
      label: '物理学', 
      children: [
        { value: 'force', label: '力' },
        { value: 'motion', label: '运动' },
        { value: 'energy', label: '能量' },
        { value: 'light', label: '光' },
        { value: 'electricity', label: '电' },
        { value: 'relativity', label: '相对论' },
        { value: 'quantum', label: '量子力学' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'chemistry', 
      label: '化学', 
      children: [
        { value: 'elements', label: '元素' },
        { value: 'matter_change', label: '物质变化' },
        { value: 'chemical_reaction', label: '化学反应' },
        { value: 'periodic_table', label: '周期表' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'earth', 
      label: '地球科学', 
      children: [
        { value: 'geology', label: '地质' },
        { value: 'volcano', label: '火山' },
        { value: 'earthquake', label: '地震' },
        { value: 'ocean', label: '海洋' },
        { value: 'meteorology', label: '气象' },
        { value: 'climate_change', label: '气候变化' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'biology', 
      label: '基础生物学', 
      children: [
        { value: 'origin_of_life', label: '生命起源' },
        { value: 'cell', label: '细胞' },
        { value: 'gene', label: '基因' },
        { value: 'evolution', label: '进化论(总纲)' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'plants', 
      label: '植物学', 
      children: [
        { value: 'photosynthesis', label: '光合作用' },
        { value: 'trees', label: '树木' },
        { value: 'flowers', label: '花卉' },
        { value: 'plant_classification', label: '植物分类' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'animals', 
      label: '动物学', 
      children: [
        { value: 'mammals', label: '哺乳动物' },
        { value: 'birds', label: '鸟类' },
        { value: 'insects', label: '昆虫' },
        { value: 'marine_life', label: '海洋生物' },
        { value: 'animal_behavior', label: '动物行为' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'microbes', 
      label: '微生物学', 
      children: [
        { value: 'bacteria', label: '细菌' },
        { value: 'virus', label: '病毒' },
        { value: 'fungi', label: '真菌' },
        { value: 'microscopic_world', label: '微观世界' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'ecology', 
      label: '生态学', 
      children: [
        { value: 'ecosystem', label: '生态系统' },
        { value: 'food_chain', label: '食物链' },
        { value: 'environmental_protection', label: '环境保护' },
        { value: 'biodiversity', label: '生物多样性' },
        { value: 'other', label: '其他' }
      ] 
    },
    { 
      value: 'human', 
      label: '人体科学', 
      children: [
        { value: 'organ_system', label: '器官系统' },
        { value: 'brain', label: '大脑' },
        { value: 'senses', label: '感官' },
        { value: 'health_disease', label: '健康与疾病' },
        { value: 'other', label: '其他' }
      ] 
    }
  ];

  // 加载视频列表
  useEffect(() => {
    loadVideos();
  }, []);

  // 加载视频列表
  const loadVideos = async () => {
    try {
      setLoading(true);
      console.log('[词汇管理] 开始加载视频列表...');
      const response = await videoAPI.getVideos();
      console.log('[词汇管理] 视频API响应:', response);
      if (response.data.success) {
        const videosFromServer = response.data.videos;
        console.log('[词汇管理] 从服务器获取到的视频:', videosFromServer);
        // 转换后端返回的视频数据格式，使其与前端期望的格式匹配
        const formattedVideos = videosFromServer.map(video => {
          const videoId = video.videoId;
          const videoUrl = video.videoUrl;
          let subtitleUrl = '';
          
          if (video.subtitleFiles && video.subtitleFiles.length > 0) {
            const subtitleFile = video.subtitleFiles[0];
            console.log('[词汇管理] 处理字幕文件:', subtitleFile);
            // 如果已经是完整URL，直接使用
            if (subtitleFile.startsWith('http')) {
              subtitleUrl = subtitleFile;
            } else if (videoUrl && videoUrl.includes('cos.')) {
              const baseUrl = videoUrl.substring(0, videoUrl.lastIndexOf('/') + 1);
              subtitleUrl = baseUrl + encodeURIComponent(subtitleFile);
            } else {
              subtitleUrl = `/uploads/${subtitleFile}`;
            }
          }
          
          return {
            id: videoId,
            title: video.title,
            category: video.category || 'human',
            subcategory: video.subcategory || '',
            level: 'intermediate',
            fileUrl: videoUrl,
            subtitleUrl: subtitleUrl
          };
        });
        console.log('[词汇管理] 格式化后的视频列表:', formattedVideos);
        setVideos(formattedVideos);
        filterVideos(formattedVideos, selectedCategory, selectedSubcategory);
        
        // 优先检查videoIdParam，然后检查lessonId
        const targetVideoId = videoIdParam || lessonId;
        if (targetVideoId) {
          const video = formattedVideos.find(v => v.id === targetVideoId);
          if (video) {
            setSelectedVideo(video);
            loadSubtitles(video);
          }
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('加载视频列表失败:', error);
      setError('加载视频列表失败');
      setLoading(false);
    }
  };

  // 筛选视频
  const filterVideos = (videoList, category, subcategory) => {
    console.log('[词汇管理] 筛选视频 - 输入列表:', videoList.length, '分类:', category, '子分类:', subcategory);
    let filtered = videoList;
    
    // 先按主分类筛选
    if (category !== 'all') {
      filtered = filtered.filter(video => video.category === category);
      console.log('[词汇管理] 按主分类筛选后:', filtered.length);
    }
    
    // 再按子分类筛选
    if (subcategory !== 'all') {
      filtered = filtered.filter(video => video.subcategory === subcategory);
      console.log('[词汇管理] 按子分类筛选后:', filtered.length);
    }
    
    console.log('[词汇管理] 筛选结果:', filtered.length, '个视频');
    setFilteredVideos(filtered);
  };

  // 处理分类选择
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    setSelectedSubcategory('all'); // 重置子分类
    filterVideos(videos, category, 'all');
  };

  // 处理子分类选择
  const handleSubcategoryChange = (e) => {
    const subcategory = e.target.value;
    setSelectedSubcategory(subcategory);
    filterVideos(videos, selectedCategory, subcategory);
  };

  // 加载字幕
  const loadSubtitles = async (video) => {
    if (!video) {
      setSubtitles([]);
      return;
    }
    console.log('[词汇管理] 开始加载字幕，视频:', video);

    try {
      setLoading(true);
      
      // 从后端获取视频详细信息
      console.log('[词汇管理] 从后端获取视频详情，video.id:', video.id);
      const videoResponse = await videoAPI.getVideo(video.id);
      console.log('[词汇管理] 视频详情响应:', videoResponse);
      let subtitleUrl = video.subtitleUrl;
      
      if (videoResponse.data.success && videoResponse.data.video) {
        const videoDetails = videoResponse.data.video;
        console.log('[词汇管理] 视频详情:', videoDetails);
        if (videoDetails.subtitleFiles && videoDetails.subtitleFiles.length > 0) {
          const subtitleFile = videoDetails.subtitleFiles[0];
          console.log('[词汇管理] 字幕文件:', subtitleFile);
          // 如果已经是完整URL，直接使用
          if (subtitleFile.startsWith('http')) {
            subtitleUrl = subtitleFile;
          } else if (videoDetails.videoUrl && videoDetails.videoUrl.includes('cos.')) {
            const baseUrl = videoDetails.videoUrl.substring(0, videoDetails.videoUrl.lastIndexOf('/') + 1);
            subtitleUrl = baseUrl + encodeURIComponent(subtitleFile);
          } else {
            // 后端已经保存了 /uploads/ 前缀，直接使用
            subtitleUrl = subtitleFile.startsWith('/uploads') ? subtitleFile : `/uploads/${subtitleFile}`;
          }
        }
      }
      
      console.log('[词汇管理] 最终字幕URL:', subtitleUrl);
      if (!subtitleUrl) {
        setSubtitles([]);
        setLoading(false);
        return;
      }

      const fullUrl = subtitleUrl.startsWith('http') ? subtitleUrl : subtitleUrl;
      let encodedUrl = fullUrl;
      if (fullUrl.startsWith('http')) {
        try {
          const urlObj = new URL(fullUrl);
          if (!urlObj.pathname.includes('%20')) {
            urlObj.pathname = encodeURI(urlObj.pathname);
          }
          encodedUrl = urlObj.toString();
        } catch (e) {
          encodedUrl = fullUrl;
        }
      } else {
        if (!fullUrl.includes('%20')) {
          encodedUrl = encodeURI(fullUrl);
        }
        // 已经是 /uploads/ 开头，直接使用
      }
      console.log('[词汇管理] 编码后的URL:', encodedUrl);
      const response = await fetch(encodedUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const content = await response.text();
      console.log('[词汇管理] 字幕文件内容长度:', content.length);
      const parsedSubtitles = parseSubtitles(content, subtitleUrl);
      console.log('[词汇管理] 解析后的字幕数量:', parsedSubtitles.length);
      setSubtitles(parsedSubtitles);
      
      // 加载该视频的已保存词汇
      console.log('[词汇管理] 开始加载已保存词汇，videoId:', video.id);
      const hasSavedVocabulary = await loadSavedVocabulary(video.id);
      
      // 分析字幕词汇分布
      analyzeSubtitleVocabulary(parsedSubtitles);
      
      // 如果没有保存的词汇，自动提取
      if (!hasSavedVocabulary && parsedSubtitles.length > 0) {
        console.log('[词汇管理] 没有保存的词汇，自动提取...');
        await handleSmartExtractAuto(video, parsedSubtitles);
      }
    } catch (error) {
      console.error('加载字幕失败:', error);
      setError('加载字幕失败');
      setSubtitles([]);
    } finally {
      setLoading(false);
    }
  };

  // 解析字幕文件
  const parseSubtitles = (content, subtitleUrl) => {
    const subtitles = [];
    
    if (subtitleUrl.endsWith('.srt')) {
      // 解析SRT格式
      const blocks = content.trim().split(/\n\s*\n/);
      blocks.forEach((block, index) => {
        const lines = block.split('\n');
        if (lines.length >= 3) {
          const timeLine = lines[1];
          const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
          
          if (timeMatch) {
            const startHours = parseInt(timeMatch[1]);
            const startMinutes = parseInt(timeMatch[2]);
            const startSeconds = parseInt(timeMatch[3]);
            const startTime = startHours * 3600 + startMinutes * 60 + startSeconds;
            
            const textLines = lines.slice(2);
            let english = '';
            let chinese = '';
            
            textLines.forEach(line => {
              if (/[\u4e00-\u9fa5]/.test(line)) {
                chinese += (chinese ? ' ' : '') + line;
              } else if (line.trim()) {
                english += (english ? ' ' : '') + line;
              }
            });
            
            subtitles.push({
              id: index + 1,
              time: startTime,
              english: english.trim(),
              chinese: chinese.trim()
            });
          }
        }
      });
    } else if (subtitleUrl.endsWith('.vtt')) {
      // 解析VTT格式
      const lines = content.trim().split('\n');
      let i = 0;
      
      while (i < lines.length && !lines[i].includes('-->')) {
        i++;
      }
      
      let subtitleIndex = 1;
      while (i < lines.length) {
        const timeLine = lines[i];
        const timeMatch = timeLine.match(/(\d{2}):(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2}):(\d{2})\.(\d{3})/);
        
        if (!timeMatch) {
          const shortTimeMatch = timeLine.match(/(\d{2}):(\d{2})\.(\d{3})\s*-->\s*(\d{2}):(\d{2})\.(\d{3})/);
          if (shortTimeMatch) {
            const startMinutes = parseInt(shortTimeMatch[1]);
            const startSeconds = parseInt(shortTimeMatch[2]);
            const startTime = startMinutes * 60 + startSeconds;
            
            i++;
            let english = '';
            let chinese = '';
            
            while (i < lines.length && lines[i].trim() !== '') {
              const line = lines[i];
              if (/[\u4e00-\u9fa5]/.test(line)) {
                chinese += (chinese ? ' ' : '') + line;
              } else if (line.trim()) {
                english += (english ? ' ' : '') + line;
              }
              i++;
            }
            
            subtitles.push({
              id: subtitleIndex++,
              time: startTime,
              english: english.trim(),
              chinese: chinese.trim()
            });
          }
          i++;
          continue;
        }
        
        const startHours = parseInt(timeMatch[1]);
        const startMinutes = parseInt(timeMatch[2]);
        const startSeconds = parseInt(timeMatch[3]);
        const startTime = startHours * 3600 + startMinutes * 60 + startSeconds;
        
        i++;
        let english = '';
        let chinese = '';
        
        while (i < lines.length && lines[i].trim() !== '') {
          const line = lines[i];
          if (/[\u4e00-\u9fa5]/.test(line)) {
            chinese += (chinese ? ' ' : '') + line;
          } else if (line.trim()) {
            english += (english ? ' ' : '') + line;
          }
          i++;
        }
        
        subtitles.push({
          id: subtitleIndex++,
          time: startTime,
          english: english.trim(),
          chinese: chinese.trim()
        });
        
        while (i < lines.length && lines[i].trim() === '') {
          i++;
        }
      }
    }
    
    return subtitles;
  };

  // 加载已保存的词汇，返回是否有保存的词汇
  const loadSavedVocabulary = async (videoId) => {
    try {
      console.log('[词汇管理] 从后端API加载词汇，videoId:', videoId);
      
      // 先尝试从后端API获取
      const response = await fetch(`/api/vocabulary/video/${videoId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.vocabulary) {
          console.log('[词汇管理] 从后端获取的词汇数据:', data.vocabulary);
          
          const words = (data.vocabulary.words || []).map(w => ({
            ...w,
            id: w.id || w._id || Date.now() + Math.random()
          }));
          const phrases = (data.vocabulary.phrases || []).map(p => ({
            ...p,
            id: p.id || p._id || Date.now() + Math.random()
          }));
          const expressions = (data.vocabulary.expressions || []).map(e => ({
            ...e,
            id: e.id || e._id || Date.now() + Math.random(),
            expression: e.expression || e.word || ''
          }));
          
          setSelectedWords(words);
          setSelectedPhrases(phrases);
          setSelectedExpressions(expressions);
          
          const hasData = words.length > 0 || phrases.length > 0 || expressions.length > 0;
          if (hasData) {
            return true;
          }
        }
      }
      
      // 如果后端没有数据，尝试从localStorage获取
      console.log('[词汇管理] 后端无数据，尝试从localStorage加载');
      const savedVocabulary = JSON.parse((localStorage.getItem(`adminVocabulary_${videoId}`) || '{}').replace(/^"undefined"$/, '{}'));
      console.log('[词汇管理] 已保存的词汇数据:', savedVocabulary);
      
      const hasWords = (savedVocabulary.words || []).length > 0;
      const hasPhrases = (savedVocabulary.phrases || []).length > 0;
      const hasExpressions = (savedVocabulary.expressions || []).length > 0;
      
      setSelectedWords(savedVocabulary.words || []);
      setSelectedPhrases(savedVocabulary.phrases || []);
      setSelectedExpressions(savedVocabulary.expressions || []);
      
      return hasWords || hasPhrases || hasExpressions;
    } catch (error) {
      console.error('加载已保存词汇失败:', error);
      return false;
    }
  };
  
  // 自动智能提取词汇（不显示弹窗）
  const handleSmartExtractAuto = async (video, subtitleList) => {
    try {
      console.log('[词汇管理] 开始自动智能提取词汇...');
      const subtitleContent = subtitleList.map(s => `${s.english}\n${s.chinese}`).join('\n');
      
      const response = await vocabularyAPI.smartExtract({
        subtitleContent,
        subtitleUrl: video.subtitleUrl,
        videoId: video.id
      });

      if (response.data.success) {
        const { words, phrases, scienceVocabulary } = response.data;
        
        setSelectedWords(words.map(w => ({
          id: Date.now() + Math.random(),
          ...w,
          subtitleId: null,
          sentence: '',
          sentenceChinese: ''
        })));

        setSelectedPhrases(phrases.map(p => ({
          id: Date.now() + Math.random(),
          ...p,
          subtitleId: null,
          sentence: '',
          sentenceChinese: ''
        })));

        setSelectedExpressions(scienceVocabulary.map(e => ({
          id: Date.now() + Math.random(),
          ...e,
          subtitleId: null,
          sentence: '',
          sentenceChinese: ''
        })));

        console.log('[词汇管理] 自动智能提取完成');
      }
    } catch (error) {
      console.error('[词汇管理] 自动智能提取失败:', error);
    }
  };

  // 智能提取词汇
  const handleSmartExtract = async () => {
    if (!selectedVideo) {
      alert('请先选择一个视频');
      return;
    }

    try {
      setExtractingVocabulary(true);
      console.log('[词汇管理] 开始智能提取词汇...');

      const subtitleContent = subtitles.map(s => `${s.english}\n${s.chinese}`).join('\n');
      const response = await vocabularyAPI.smartExtract({
        subtitleContent,
        subtitleUrl: selectedVideo.subtitleUrl,
        videoId: selectedVideo.id
      });

      if (response.data.success) {
        const { words, phrases, scienceVocabulary } = response.data;
        
        setSelectedWords(words.map(w => ({
          id: Date.now() + Math.random(),
          ...w,
          subtitleId: null,
          sentence: '',
          sentenceChinese: ''
        })));

        setSelectedPhrases(phrases.map(p => ({
          id: Date.now() + Math.random(),
          ...p,
          subtitleId: null,
          sentence: '',
          sentenceChinese: ''
        })));

        setSelectedExpressions(scienceVocabulary.map(e => ({
          id: Date.now() + Math.random(),
          ...e,
          subtitleId: null,
          sentence: '',
          sentenceChinese: ''
        })));

        alert('智能词汇提取成功！');
        console.log('[词汇管理] 智能提取完成:', response.data);
      } else {
        alert('智能提取失败: ' + (response.data.error || '未知错误'));
      }
    } catch (error) {
      console.error('[词汇管理] 智能提取失败:', error);
      alert('智能提取失败，请查看控制台');
    } finally {
      setExtractingVocabulary(false);
    }
  };
  
  // 分析字幕词汇分布
  const analyzeSubtitleVocabulary = async (subtitles) => {
    try {
      console.log('[词汇管理] 开始分析字幕词汇分布');
      // 提取所有字幕的英文文本
      const subtitleContent = subtitles.map(subtitle => subtitle.english).join(' ');
      
      // 调用词汇分析API
      const analyzeResponse = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/vocabulary/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subtitleContent })
      });
      
      if (analyzeResponse.ok) {
        const analyzeData = await analyzeResponse.json();
        if (analyzeData.success && analyzeData.analysis) {
          console.log('[词汇管理] 词汇分析结果:', analyzeData.analysis);
          setVocabularyAnalysis(analyzeData.analysis);
        }
      }
    } catch (error) {
      console.error('分析字幕词汇失败:', error);
    }
  };

  // 从句子中提取单词
  const extractWordsFromSentence = (sentence) => {
    return sentence.match(/\b[a-zA-Z]+(?:[''][a-zA-Z]+)?\b/g) || [];
  };

  // 从句子中提取可能的短语（连续的2-4个单词组合）
  const extractPhrasesFromSentence = (sentence) => {
    const words = sentence.match(/\b[a-zA-Z]+(?:[''][a-zA-Z]+)?\b/g) || [];
    const phrases = [];
    // 提取2-4个单词的组合
    for (let len = 2; len <= 4; len++) {
      for (let i = 0; i <= words.length - len; i++) {
        const phrase = words.slice(i, i + len).join(' ');
        phrases.push(phrase);
      }
    }
    return phrases;
  };

  // 处理视频选择
  const handleVideoSelect = (video) => {
    console.log('[词汇管理] 选择视频:', video);
    setSelectedVideo(video);
    setSelectedWords([]);
    setSelectedPhrases([]);
    setSelectedExpressions([]);
    loadSubtitles(video);
  };

  // 获取单词完整信息（音标、词性、翻译、难度）
  const getWordInfo = async (word) => {
    try {
      // 使用Dictionary API获取单词信息
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${encodeURIComponent(word)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          const wordData = data[0];
          
          // 获取音标
          let phonetic = '';
          if (wordData.phonetic) {
            phonetic = wordData.phonetic;
          } else if (wordData.phonetics && wordData.phonetics.length > 0) {
            for (const p of wordData.phonetics) {
              if (p.text) {
                phonetic = p.text;
                break;
              }
            }
          }
          
          // 获取词性
          let partOfSpeech = '';
          if (wordData.meanings && wordData.meanings.length > 0) {
            partOfSpeech = wordData.meanings[0].partOfSpeech || '';
          }
          
          // 获取英文定义
          let definition = '';
          if (wordData.meanings && wordData.meanings.length > 0 && wordData.meanings[0].definitions && wordData.meanings[0].definitions.length > 0) {
            definition = wordData.meanings[0].definitions[0].definition || '';
          }
          
          // 获取中文翻译
          let chineseTranslation = '';
          try {
            const translateResponse = await fetch(
              `https://api.mymemory.translated.net/get?q=${encodeURIComponent(word)}&langpair=en|zh-CN`
            );
            if (translateResponse.ok) {
              const translateData = await translateResponse.json();
              if (translateData.responseStatus === 200 && translateData.responseData) {
                chineseTranslation = translateData.responseData.translatedText;
              }
            }
          } catch (translateError) {
            console.log('翻译API调用失败，使用默认翻译');
          }
          
          // 映射到剑桥等级
          let cambridgeLevel = 'Starters';
          if (word.length > 8) {
            cambridgeLevel = 'FCE';
          } else if (word.length > 6) {
            cambridgeLevel = 'PET';
          } else if (word.length > 4) {
            cambridgeLevel = 'KET';
          } else if (word.length > 2) {
            cambridgeLevel = 'Flyers';
          } else if (word.length > 1) {
            cambridgeLevel = 'Movers';
          }
          
          return {
            phonetic,
            partOfSpeech,
            definition,
            chineseTranslation,
            cambridgeLevel
          };
        }
      }
    } catch (error) {
      console.error('获取单词信息失败:', error);
    }
    return {
      phonetic: '',
      partOfSpeech: '',
      definition: '',
      chineseTranslation: '',
      cambridgeLevel: 'Starters'
    };
  };

  // 获取短语中每个单词的音标
  const getPhrasePhonetics = async (phrase) => {
    const words = phrase.split(' ');
    const phonetics = [];
    
    for (const word of words) {
      const wordInfo = await getWordInfo(word);
      phonetics.push({
        word,
        phonetic: wordInfo.phonetic
      });
    }
    
    return phonetics;
  };

  // 处理单词选择
  const handleWordSelect = async (word, subtitleId) => {
    const existingWord = selectedWords.find(w => w.word === word);
    if (!existingWord) {
      const subtitle = subtitles.find(s => s.id === subtitleId);
      const wordInfo = await getWordInfo(word);
      setSelectedWords([...selectedWords, { 
        id: Date.now() + Math.random(), 
        word, 
        phonetic: wordInfo.phonetic,
        partOfSpeech: wordInfo.partOfSpeech,
        definition: wordInfo.definition,
        chineseTranslation: wordInfo.chineseTranslation,
        synonyms: '',
        cambridgeLevel: wordInfo.cambridgeLevel,
        subtitleId, 
        sentence: subtitle?.english || '',
        sentenceChinese: subtitle?.chinese || ''
      }]);
    }
  };

  // 处理短语选择
  const handlePhraseSelect = async (phrase, subtitleId) => {
    const existingPhrase = selectedPhrases.find(p => p.phrase === phrase);
    if (!existingPhrase) {
      const subtitle = subtitles.find(s => s.id === subtitleId);
      // 获取短语中每个单词的音标
      const phonetics = await getPhrasePhonetics(phrase);
      setSelectedPhrases([...selectedPhrases, { 
        id: Date.now() + Math.random(), 
        phrase, 
        phonetic: phonetics.map(p => p.phonetic).filter(Boolean).join(' '),
        phonetics,
        chineseTranslation: '',
        definition: '',
        synonyms: '',
        subtitleId, 
        sentence: subtitle?.english || '',
        sentenceChinese: subtitle?.chinese || ''
      }]);
    }
  };

  // 处理自然科普词汇选择
  const handleExpressionSelect = async (expression, subtitleId) => {
    const existingExpression = selectedExpressions.find(e => e.expression === expression);
    if (!existingExpression) {
      const subtitle = subtitles.find(s => s.id === subtitleId);
      // 获取自然科普词汇中每个单词的音标
      const phonetics = await getPhrasePhonetics(expression);
      setSelectedExpressions([...selectedExpressions, { 
        id: Date.now() + Math.random(), 
        expression, 
        phonetic: phonetics.map(p => p.phonetic).filter(Boolean).join(' '),
        phonetics,
        chineseTranslation: '',
        definition: '',
        synonyms: '',
        subtitleId, 
        sentence: subtitle?.english || '',
        sentenceChinese: subtitle?.chinese || ''
      }]);
    }
  };

  // 保存选择的词汇
  const handleSave = async () => {
    if (!selectedVideo) {
      alert('请先选择一个视频');
      return;
    }
    
    console.log('[词汇管理] 保存词汇...');
    console.log('[词汇管理] 选择的单词:', selectedWords);
    console.log('[词汇管理] 选择的短语:', selectedPhrases);
    console.log('[词汇管理] 选择的自然科普词汇:', selectedExpressions);
    
    try {
      // 调用后端API保存词汇到数据库
      const response = await fetch(`/api/vocabulary/video/${selectedVideo.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          words: selectedWords,
          phrases: selectedPhrases,
          scienceVocabulary: selectedExpressions,
          recommendedLevel: 'Starters'
        })
      });
      
      if (response.ok) {
        // 同时保存到localStorage作为备份
        const vocabularyData = {
          words: selectedWords,
          phrases: selectedPhrases,
          expressions: selectedExpressions,
          videoId: selectedVideo.id,
          videoTitle: selectedVideo.title
        };
        localStorage.setItem(`adminVocabulary_${selectedVideo.id}`, JSON.stringify(vocabularyData));
        
        alert('词汇保存成功！');
        navigate('/admin/manage');
      } else {
        const error = await response.json();
        alert('保存失败: ' + (error.error || '未知错误'));
      }
    } catch (error) {
      console.error('[词汇管理] 保存失败:', error);
      alert('保存失败: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', color: '#1a2b4c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
          <h2 style={{ fontSize: '2rem', marginBottom: '1rem' }}>加载中</h2>
          <p style={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.6)' }}>正在加载字幕数据，请稍候...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', color: '#1a2b4c' }}>
      {/* 导航栏 */}
      <nav style={{ 
        position: 'sticky', 
        top: 0, 
        zIndex: 1000, 
        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
        backdropFilter: 'blur(10px)',
        padding: '1rem 0',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div className="container">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            maxWidth: '1600px',
            margin: '0 auto'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem',
              textDecoration: 'none',
              color: '#1a2b4c'
            }}>
              <img 
                src="/logo.png" 
                alt="123wonder英文科普" 
                style={{ 
                  height: '50px', 
                  width: 'auto'
                }} 
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <button 
                onClick={() => navigate('/admin/upload')}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                上传文件
              </button>
              <button 
                onClick={() => navigate('/admin/manage')}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                管理文件
              </button>
              <button 
                onClick={() => navigate('/admin/invitation')}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                邀请码管理
              </button>
              <button 
                onClick={() => navigate('/admin/users')}
                style={{
                  background: 'none',
                  border: '1px solid #e9ecef',
                  color: '#1a2b4c',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  fontWeight: '500',
                  transition: 'all 0.3s ease',
                  fontSize: '1.1rem'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                用户管理
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div style={{ padding: '2rem 0' }}>
        <div className="container" style={{ maxWidth: '1600px', margin: '0 auto', padding: '0 1.5rem' }}>
          <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1a2b4c', marginBottom: '2rem' }}>词汇管理</h1>
          
          {error && (
            <div style={{ 
              padding: '1rem', 
              marginBottom: '1.5rem', 
              backgroundColor: '#f8d7da', 
              color: '#721c24', 
              border: '1px solid #f5c6cb', 
              borderRadius: '8px'
            }}>
              {error}
            </div>
          )}

          {/* 只有在非直接进入模式下才显示分类筛选和视频选择 */}
          {!isDirectEntry && (
            <>
              {/* 分类筛选 */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#495057',
                  fontWeight: '500',
                  fontSize: '1rem'
                }}>选择分类</label>
                <select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* 子分类筛选 */}
              {selectedCategory !== 'all' && (
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ 
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: '#495057',
                    fontWeight: '500',
                    fontSize: '1rem'
                  }}>选择详细分类</label>
                  <select
                    value={selectedSubcategory}
                    onChange={handleSubcategoryChange}
                    style={{
                      width: '100%',
                      maxWidth: '600px',
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      border: '1px solid #e9ecef',
                      color: '#495057',
                      fontSize: '1rem',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <option value="all">全部</option>
                    {categories.find(cat => cat.value === selectedCategory)?.children.map((subcategory) => (
                      <option key={subcategory.value} value={subcategory.value}>
                        {subcategory.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* 视频选择下拉菜单 */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: '#495057',
                  fontWeight: '500',
                  fontSize: '1rem'
                }}>选择视频</label>
                <select
                  value={selectedVideo?.id || ''}
                  onChange={(e) => {
                    const videoId = e.target.value;
                    if (videoId) {
                      const video = videos.find(v => v.id === videoId);
                      if (video) {
                        handleVideoSelect(video);
                      }
                    }
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '600px',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    border: '1px solid #e9ecef',
                    color: '#495057',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <option value="">请选择一个视频</option>
                  {filteredVideos.map((video) => (
                    <option key={video.id} value={video.id}>
                      {video.title} ({video.category} - {video.level})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* 直接进入模式下显示当前编辑的视频标题 */}
          {isDirectEntry && selectedVideo && (
            <div style={{ 
              marginBottom: '2rem', 
              padding: '1.5rem', 
              backgroundColor: '#e3f2fd', 
              borderRadius: '8px',
              border: '1px solid #bbdefb'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#1565c0', fontSize: '1.2rem' }}>
                📚 正在编辑词汇
              </h3>
              <p style={{ margin: '0', color: '#1a2b4c', fontSize: '1.1rem' }}>
                <strong>{selectedVideo.title}</strong>
              </p>
              <button
                onClick={() => navigate('/admin/manage')}
                style={{
                  marginTop: '1rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  border: '1px solid #1976d2',
                  color: '#1976d2',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.1)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ← 返回文件管理
              </button>
            </div>
          )}

          {selectedVideo ? (
            <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem' }}>
              <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1a2b4c', marginBottom: '1.5rem' }}>字幕列表 - {selectedVideo.title}</h2>
                
                {subtitles.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <button
                      onClick={handleSmartExtract}
                      disabled={extractingVocabulary}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1.5rem',
                        backgroundColor: extractingVocabulary ? '#90caf9' : '#4caf50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: extractingVocabulary ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {extractingVocabulary ? '正在智能提取词汇...' : '🤖 智能提取词汇'}
                    </button>
                    <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem', textAlign: 'center' }}>
                      自动从词汇库提取 10 个单词、5 个短语、5 个自然科普词汇
                    </p>
                  </div>
                )}
                
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {subtitles.length > 0 ? (
                    subtitles.map((subtitle) => {
                      const words = extractWordsFromSentence(subtitle.english);
                      return (
                        <div key={subtitle.id} style={{ 
                          marginBottom: '1.5rem', 
                          padding: '1rem', 
                          borderRadius: '8px', 
                          backgroundColor: 'rgba(240, 240, 240, 0.3)', 
                          border: '1px solid #e0e0e0'
                        }}>
                          <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.5)', marginBottom: '0.5rem' }}>
                            {Math.floor(subtitle.time / 60).toString().padStart(2, '0')}:{(subtitle.time % 60).toString().padStart(2, '0')}
                          </div>
                          <div style={{ fontSize: '1rem', color: '#333', marginBottom: '0.5rem' }}>
                            {subtitle.english}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)', marginBottom: '1rem' }}>
                            {subtitle.chinese}
                          </div>
                          <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>单词选择</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                              {words.map((word, index) => (
                                <button 
                                  key={index}
                                  onClick={() => handleWordSelect(word, subtitle.id)}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: selectedWords.some(w => w.word === word) ? 'rgba(25, 118, 210, 0.1)' : 'white',
                                    color: selectedWords.some(w => w.word === word) ? '#1976d2' : '#333',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {word}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>短语选择</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              {extractPhrasesFromSentence(subtitle.english).slice(0, 15).map((phrase, index) => (
                                <button 
                                  key={index}
                                  onClick={() => handlePhraseSelect(phrase, subtitle.id)}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: selectedPhrases.some(p => p.phrase === phrase) ? 'rgba(25, 118, 210, 0.1)' : 'white',
                                    color: selectedPhrases.some(p => p.phrase === phrase) ? '#1976d2' : '#333',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {phrase}
                                </button>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="text"
                                placeholder="或手动输入短语"
                                id={`phraseInput_${subtitle.id}`}
                                style={{
                                  flex: 1,
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '4px',
                                  border: '1px solid #e0e0e0',
                                  fontSize: '0.8rem'
                                }}
                              />
                              <button
                                onClick={() => {
                                  const input = document.getElementById(`phraseInput_${subtitle.id}`);
                                  if (input && input.value.trim()) {
                                    handlePhraseSelect(input.value.trim(), subtitle.id);
                                    input.value = '';
                                  }
                                }}
                                style={{
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '4px',
                                  border: '1px solid #1976d2',
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  color: '#1976d2',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  transition: 'all 0.2s'
                                }}
                              >
                                添加
                              </button>
                            </div>
                          </div>
                          <div>
                            <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#333', marginBottom: '0.5rem' }}>自然科普词汇选择</h4>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              {extractWordsFromSentence(subtitle.english).map((expression, index) => (
                                <button 
                                  key={index}
                                  onClick={() => handleExpressionSelect(expression, subtitle.id)}
                                  style={{
                                    padding: '0.3rem 0.6rem',
                                    borderRadius: '4px',
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: selectedExpressions.some(e => e.expression === expression) ? 'rgba(25, 118, 210, 0.1)' : 'white',
                                    color: selectedExpressions.some(e => e.expression === expression) ? '#1976d2' : '#333',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {expression}
                                </button>
                              ))}
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <input
                                type="text"
                                placeholder="或手动输入自然科普词汇"
                                id={`expressionInput_${subtitle.id}`}
                                style={{
                                  flex: 1,
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '4px',
                                  border: '1px solid #e0e0e0',
                                  fontSize: '0.8rem'
                                }}
                              />
                              <button
                                onClick={() => {
                                  const input = document.getElementById(`expressionInput_${subtitle.id}`);
                                  if (input && input.value.trim()) {
                                    handleExpressionSelect(input.value.trim(), subtitle.id);
                                    input.value = '';
                                  }
                                }}
                                style={{
                                  padding: '0.3rem 0.6rem',
                                  borderRadius: '4px',
                                  border: '1px solid #1976d2',
                                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                  color: '#1976d2',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  transition: 'all 0.2s'
                                }}
                              >
                                添加
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ textAlign: 'center', padding: '4rem', color: '#6c757d' }}>
                      <p>该视频暂无字幕，请先上传字幕文件</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div style={{ width: '350px', backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1a2b4c', marginBottom: '1.5rem' }}>已选择的词汇</h2>
                
                {/* 词汇分布统计 */}
                {vocabularyAnalysis && (
                  <div style={{ 
                    marginBottom: '2rem', 
                    padding: '1rem', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px', 
                    border: '1px solid #e0e0e0'
                  }}>
                    <h3 style={{ margin: '0 0 1rem 0', color: '#333', fontSize: '1rem', fontWeight: '600' }}>词汇分布统计</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', tableLayout: 'fixed' }}>
                        <thead>
                          {(() => {
                            const levelCounts = vocabularyAnalysis.levelCounts;
                            const maxCount = Math.max(...Object.values(levelCounts).filter(count => count !== levelCounts.Other));
                            const maxLevel = ['Starters', 'Movers', 'Flyers', 'KET', 'PET', 'FCE'].find(level => levelCounts[level] === maxCount);
                            return (
                              <tr style={{ backgroundColor: '#f1f1f1' }}>
                                {[
                                  { name: 'Starters' },
                                  { name: 'Movers' },
                                  { name: 'Flyers' },
                                  { name: 'KET' },
                                  { name: 'PET' },
                                  { name: 'FCE' },
                                  { name: 'Other' }
                                ].map((level) => {
                                  const isMax = level.name === maxLevel && maxCount > 0;
                                  return (
                                    <th key={level.name} style={{
                                      padding: '0.5rem', 
                                      border: '1px solid #e0e0e0', 
                                      textAlign: 'center', 
                                      fontWeight: isMax ? '700' : '600',
                                      color: isMax ? '#1a73e8' : '#475569'
                                    }}>
                                      {level.name === 'Other' ? '其他' : level.name}
                                    </th>
                                  );
                                })}
                              </tr>
                            );
                          })()}
                        </thead>
                        <tbody>
                          <tr>
                            {(() => {
                              const levelCounts = vocabularyAnalysis.levelCounts;
                              const maxCount = Math.max(...Object.values(levelCounts).filter(count => count !== levelCounts.Other));
                              return [
                                { name: 'Starters', count: levelCounts.Starters },
                                { name: 'Movers', count: levelCounts.Movers },
                                { name: 'Flyers', count: levelCounts.Flyers },
                                { name: 'KET', count: levelCounts.KET },
                                { name: 'PET', count: levelCounts.PET },
                                { name: 'FCE', count: levelCounts.FCE },
                                { name: 'Other', count: levelCounts.Other }
                              ].map((level) => (
                                <td 
                                  key={level.name} 
                                  style={{
                                    padding: '0.5rem', 
                                    border: '1px solid #e0e0e0', 
                                    textAlign: 'center',
                                    ...(level.name !== 'Other' && level.count === maxCount ? { 
                                      backgroundColor: 'rgba(26, 115, 232, 0.1)', 
                                      color: '#1a73e8', 
                                      fontWeight: '600' 
                                    } : {})
                                  }}
                                >
                                  {level.count}
                                </td>
                              ));
                            })()}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                      推荐级别: {vocabularyAnalysis.recommendedLevel}
                    </div>
                  </div>
                )}
                
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333', marginBottom: '1rem' }}>单词 ({selectedWords.length})</h3>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedWords.map((word) => (
                      <div key={word.id} style={{ 
                        padding: '1rem', 
                        marginBottom: '1rem', 
                        backgroundColor: 'rgba(240, 240, 240, 0.3)', 
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.3rem' }}>{word.word}</div>
                            {word.phonetic && (
                              <div style={{ fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)', fontStyle: 'italic', marginBottom: '0.3rem' }}>
                                音标: {word.phonetic}
                              </div>
                            )}
                            {word.partOfSpeech && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                词性: {word.partOfSpeech}
                              </div>
                            )}
                            {word.chineseTranslation && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                翻译: {word.chineseTranslation}
                              </div>
                            )}
                            {word.definition && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                英文定义: {word.definition}
                              </div>
                            )}
                            {word.synonyms && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                联想词: {word.synonyms}
                              </div>
                            )}
                            {word.cambridgeLevel && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginTop: '-0.3rem' }}>
                                剑桥等级: {word.cambridgeLevel}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button 
                              onClick={() => {
                                setEditingWord(word);
                                setEditForm({
                                  phonetic: word.phonetic || '',
                                  partOfSpeech: word.partOfSpeech || '',
                                  definition: word.definition || '',
                                  chineseTranslation: word.chineseTranslation || '',
                                  synonyms: word.synonyms || '',
                                  cambridgeLevel: word.cambridgeLevel || '剑桥一级'
                                });
                              }}
                              style={{
                                background: 'none',
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.1)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              编辑
                            </button>
                            <button 
                              onClick={() => setSelectedWords(selectedWords.filter(w => w.id !== word.id))}
                              style={{
                                background: 'none',
                                border: '1px solid #dc3545',
                                color: '#dc3545',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.1)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {selectedWords.length === 0 && (
                      <p style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '0.9rem' }}>暂无选择的单词</p>
                    )}
                  </div>
                </div>
                
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333', marginBottom: '1rem' }}>短语 ({selectedPhrases.length})</h3>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedPhrases.map((phrase) => (
                      <div key={phrase.id} style={{ 
                        padding: '1rem', 
                        marginBottom: '1rem', 
                        backgroundColor: 'rgba(240, 240, 240, 0.3)', 
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.3rem' }}>{phrase.phrase}</div>
                            {phrase.phonetic && (
                              <div style={{ fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)', fontStyle: 'italic', marginBottom: '0.3rem' }}>
                                音标: {phrase.phonetic}
                              </div>
                            )}
                            {phrase.partOfSpeech && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                词性: {phrase.partOfSpeech}
                              </div>
                            )}
                            {phrase.chineseTranslation && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                翻译: {phrase.chineseTranslation}
                              </div>
                            )}
                            {phrase.definition && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                英文定义: {phrase.definition}
                              </div>
                            )}
                            {phrase.synonyms && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)' }}>
                                联想词: {phrase.synonyms}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button 
                              onClick={() => {
                                setEditingPhrase(phrase);
                                setPhraseEditForm({
                                  phonetic: phrase.phonetic || '',
                                  partOfSpeech: phrase.partOfSpeech || '',
                                  chineseTranslation: phrase.chineseTranslation || '',
                                  definition: phrase.definition || '',
                                  synonyms: phrase.synonyms || ''
                                });
                              }}
                              style={{
                                background: 'none',
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.1)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              编辑
                            </button>
                            <button 
                              onClick={() => setSelectedPhrases(selectedPhrases.filter(p => p.id !== phrase.id))}
                              style={{
                                background: 'none',
                                border: '1px solid #dc3545',
                                color: '#dc3545',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.1)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {selectedPhrases.length === 0 && (
                      <p style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '0.9rem' }}>暂无选择的短语</p>
                    )}
                  </div>
                </div>
                
                <div style={{ marginBottom: '2rem' }}>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333', marginBottom: '1rem' }}>自然科普词汇 ({selectedExpressions.length})</h3>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {selectedExpressions.map((expression) => (
                      <div key={expression.id} style={{ 
                        padding: '1rem', 
                        marginBottom: '1rem', 
                        backgroundColor: 'rgba(240, 240, 240, 0.3)', 
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.3rem' }}>{expression.expression}</div>
                            {expression.phonetic && (
                              <div style={{ fontSize: '0.9rem', color: 'rgba(0, 0, 0, 0.6)', fontStyle: 'italic', marginBottom: '0.3rem' }}>
                                音标: {expression.phonetic}
                              </div>
                            )}
                            {expression.partOfSpeech && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                词性: {expression.partOfSpeech}
                              </div>
                            )}
                            {expression.chineseTranslation && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                翻译: {expression.chineseTranslation}
                              </div>
                            )}
                            {expression.definition && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)', marginBottom: '0.3rem' }}>
                                英文定义: {expression.definition}
                              </div>
                            )}
                            {expression.synonyms && (
                              <div style={{ fontSize: '0.8rem', color: 'rgba(0, 0, 0, 0.7)' }}>
                                联想词: {expression.synonyms}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button 
                              onClick={() => {
                                setEditingExpression(expression);
                                setExpressionEditForm({
                                  phonetic: expression.phonetic || '',
                                  partOfSpeech: expression.partOfSpeech || '',
                                  chineseTranslation: expression.chineseTranslation || '',
                                  definition: expression.definition || '',
                                  synonyms: expression.synonyms || ''
                                });
                              }}
                              style={{
                                background: 'none',
                                border: '1px solid #1976d2',
                                color: '#1976d2',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(25, 118, 210, 0.1)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              编辑
                            </button>
                            <button 
                              onClick={() => setSelectedExpressions(selectedExpressions.filter(e => e.id !== expression.id))}
                              style={{
                                background: 'none',
                                border: '1px solid #dc3545',
                                color: '#dc3545',
                                padding: '0.3rem 0.6rem',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.1)'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              删除
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {selectedExpressions.length === 0 && (
                      <p style={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '0.9rem' }}>暂无选择的自然科普词汇</p>
                    )}
                  </div>
                </div>
                
                <button 
                  onClick={handleSave}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                >
                  保存词汇
                </button>
                
                {/* 编辑单词模态框 */}
                {editingWord && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '2rem',
                      width: '90%',
                      maxWidth: '500px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#1a2b4c', marginBottom: '1.5rem' }}>
                        编辑单词: {editingWord.word}
                      </h3>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>音标</label>
                        <input
                          type="text"
                          value={editForm.phonetic}
                          onChange={(e) => setEditForm({ ...editForm, phonetic: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>词性</label>
                        <input
                          type="text"
                          value={editForm.partOfSpeech}
                          onChange={(e) => setEditForm({ ...editForm, partOfSpeech: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>中文翻译</label>
                        <input
                          type="text"
                          value={editForm.chineseTranslation}
                          onChange={(e) => setEditForm({ ...editForm, chineseTranslation: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>英文定义</label>
                        <textarea
                          value={editForm.definition}
                          onChange={(e) => setEditForm({ ...editForm, definition: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem',
                            minHeight: '100px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>联想词</label>
                        <input
                          type="text"
                          value={editForm.synonyms}
                          onChange={(e) => setEditForm({ ...editForm, synonyms: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                          placeholder="用逗号分隔多个联想词"
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>剑桥等级</label>
                        <select
                          value={editForm.cambridgeLevel}
                          onChange={(e) => setEditForm({ ...editForm, cambridgeLevel: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        >
                          <option value="Starters">Starters</option>
                          <option value="Movers">Movers</option>
                          <option value="Flyers">Flyers</option>
                          <option value="KET">KET</option>
                          <option value="PET">PET</option>
                          <option value="FCE">FCE</option>
                        </select>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setEditingWord(null)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: 'transparent',
                            color: '#666',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          取消
                        </button>
                        <button
                          onClick={() => {
                            // 更新单词数据
                            const updatedWords = selectedWords.map(word => {
                              if (word.id === editingWord.id) {
                                return {
                                  ...word,
                                  ...editForm
                                };
                              }
                              return word;
                            });
                            setSelectedWords(updatedWords);
                            setEditingWord(null);
                          }}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                        >
                          保存
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* 编辑短语模态框 */}
                {editingPhrase && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '2rem',
                      width: '90%',
                      maxWidth: '500px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#1a2b4c', marginBottom: '1.5rem' }}>
                        编辑短语: {editingPhrase.phrase}
                      </h3>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>音标</label>
                        <input
                          type="text"
                          value={phraseEditForm.phonetic}
                          onChange={(e) => setPhraseEditForm({ ...phraseEditForm, phonetic: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>词性</label>
                        <input
                          type="text"
                          value={phraseEditForm.partOfSpeech}
                          onChange={(e) => setPhraseEditForm({ ...phraseEditForm, partOfSpeech: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                          placeholder="如: v., n., adj., prep.等"
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>中文翻译</label>
                        <input
                          type="text"
                          value={phraseEditForm.chineseTranslation}
                          onChange={(e) => setPhraseEditForm({ ...phraseEditForm, chineseTranslation: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>英文定义</label>
                        <textarea
                          value={phraseEditForm.definition}
                          onChange={(e) => setPhraseEditForm({ ...phraseEditForm, definition: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem',
                            minHeight: '100px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>联想词</label>
                        <input
                          type="text"
                          value={phraseEditForm.synonyms}
                          onChange={(e) => setPhraseEditForm({ ...phraseEditForm, synonyms: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                          placeholder="用逗号分隔多个联想词"
                        />
                      </div>
                      
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setEditingPhrase(null)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: 'transparent',
                            color: '#666',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          取消
                        </button>
                        <button
                          onClick={() => {
                            // 更新短语数据
                            const updatedPhrases = selectedPhrases.map(phrase => {
                              if (phrase.id === editingPhrase.id) {
                                return {
                                  ...phrase,
                                  ...phraseEditForm
                                };
                              }
                              return phrase;
                            });
                            setSelectedPhrases(updatedPhrases);
                            setEditingPhrase(null);
                          }}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                        >
                          保存
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* 编辑自然科普词汇模态框 */}
                {editingExpression && (
                  <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '2rem',
                      width: '90%',
                      maxWidth: '500px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: '600', color: '#1a2b4c', marginBottom: '1.5rem' }}>
                        编辑自然科普词汇: {editingExpression.expression}
                      </h3>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>音标</label>
                        <input
                          type="text"
                          value={expressionEditForm.phonetic}
                          onChange={(e) => setExpressionEditForm({ ...expressionEditForm, phonetic: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>词性</label>
                        <input
                          type="text"
                          value={expressionEditForm.partOfSpeech}
                          onChange={(e) => setExpressionEditForm({ ...expressionEditForm, partOfSpeech: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                          placeholder="如: n., v., adj., adv.等"
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>中文翻译</label>
                        <input
                          type="text"
                          value={expressionEditForm.chineseTranslation}
                          onChange={(e) => setExpressionEditForm({ ...expressionEditForm, chineseTranslation: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>英文定义</label>
                        <textarea
                          value={expressionEditForm.definition}
                          onChange={(e) => setExpressionEditForm({ ...expressionEditForm, definition: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem',
                            minHeight: '100px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                      
                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>联想词</label>
                        <input
                          type="text"
                          value={expressionEditForm.synonyms}
                          onChange={(e) => setExpressionEditForm({ ...expressionEditForm, synonyms: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            borderRadius: '4px',
                            border: '1px solid #e0e0e0',
                            fontSize: '1rem'
                          }}
                          placeholder="用逗号分隔多个联想词"
                        />
                      </div>
                      
                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setEditingExpression(null)}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: 'transparent',
                            color: '#666',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          取消
                        </button>
                        <button
                          onClick={() => {
                            // 更新自然科普词汇数据
                            const updatedExpressions = selectedExpressions.map(expression => {
                              if (expression.id === editingExpression.id) {
                                return {
                                  ...expression,
                                  ...expressionEditForm
                                };
                              }
                              return expression;
                            });
                            setSelectedExpressions(updatedExpressions);
                            setEditingExpression(null);
                          }}
                          style={{
                            padding: '0.75rem 1.5rem',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#1565c0'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1976d2'}
                        >
                          保存
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '6rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎬</div>
              <h3 style={{ fontSize: '1.5rem', color: '#1a2b4c', marginBottom: '1rem' }}>请选择一个视频</h3>
              <p style={{ color: '#6c757d', fontSize: '1.1rem' }}>从上方的下拉菜单中选择一个视频，开始管理词汇</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default AdminVocabulary;